/**
 * FASE L2.1 — Provider Cmd+K globale.
 *
 * Monta:
 *   • Event listener globale Cmd+K / Ctrl+K
 *   • GlobalSearchModal
 *
 * HOTFIX PII feb-2026 — Doppia difesa contro PII leak:
 *   1. Route guard: la ricerca globale è MONTATA solo su path /admin/* (area staff).
 *      Anche se un admin visita una landing pubblica (es. /convenzioni, /aziende)
 *      con la sua sessione attiva, il pulsante NON deve apparire perché il visitatore
 *      seduto accanto potrebbe vedere i dati dei clienti.
 *   2. Role guard: anche in /admin/*, solo staff (super_admin/admin/dipendente/operatore).
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import GlobalSearchModal from "@/components/admin/GlobalSearchModal";
import { useAuth } from "@/App";

const STAFF_ROLES = new Set(["super_admin", "admin", "dipendente", "operatore"]);

export default function CommandKProvider() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const isStaffRoute = pathname.startsWith("/admin");
  const isStaffUser = user && STAFF_ROLES.has((user.role || "").toLowerCase());
  const canRender = isStaffRoute && isStaffUser;

  useEffect(() => {
    if (!canRender) {
      // Assicurati che se cambiamo route (staff → pubblica) o logout, la modale sia chiusa.
      if (open) setOpen(false);
      return;
    }
    const handler = (e) => {
      // Cmd+K (Mac) o Ctrl+K (Windows/Linux) — evita quando dentro un input non consapevole
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canRender, open]);

  if (!canRender) return null;

  return (
    <>
      {/* Trigger flottante (esposto per accessibilità + hint mobile) */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 hidden sm:flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-full shadow-lg hover:shadow-xl text-sm text-slate-700"
        data-testid="global-search-trigger"
        aria-label="Apri ricerca globale (Cmd+K)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span className="hidden md:inline">Cerca…</span>
        <kbd className="hidden md:inline-block text-[10px] px-1 py-0.5 bg-slate-100 rounded text-slate-500">⌘K</kbd>
      </button>
      <GlobalSearchModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
